<template>
  <!--
    کارت KPI: یک عدد سرتیتر + دلتای دوره + اسپارک‌لاین.
    عدد بزرگ با ارقام متناسب (نه tabular) نوشته می‌شود؛ ارقام هم‌عرض
    در اندازه‌ی بزرگ عدد را شل و بدقامت نشان می‌دهند.
  -->
  <b-card
    no-body
    class="kpi-card"
  >
    <b-card-body class="kpi-card__body">
      <div class="d-flex justify-content-between align-items-start">
        <div class="kpi-card__main">
          <div class="kpi-card__title">
            {{ kpi.Title }}
          </div>

          <div
            class="kpi-card__value"
            :title="fullValue"
          >
            {{ displayValue }}
          </div>

          <!-- دلتا: جهت «خوب» از خود متریک می‌آید، نه از علامت عدد -->
          <div class="kpi-card__delta">
            <template v-if="deltaLabel">
              <span :class="`text-${variant}`">
                <feather-icon
                  :icon="kpi.Delta > 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                  size="13"
                />
                {{ deltaLabel }}
              </span>
              <span class="kpi-card__compare">نسبت به دوره‌ی قبل</span>
            </template>
            <span
              v-else-if="hasCompare"
              class="kpi-card__compare"
            >
              دوره‌ی قبل: {{ compareValue }}
            </span>
            <span
              v-else
              class="kpi-card__compare"
            >&nbsp;</span>
          </div>
        </div>

        <b-avatar
          v-if="kpi.Icon"
          :variant="`light-${kpi.Variant || 'primary'}`"
          size="42"
          rounded
        >
          <feather-icon
            :icon="kpi.Icon"
            size="20"
          />
        </b-avatar>
      </div>

      <!-- اسپارک‌لاین: فقط شکل روند، بدون محور و بدون برچسب -->
      <vue-apex-charts
        v-if="spark"
        type="area"
        height="44"
        class="kpi-card__spark"
        :options="sparkOptions"
        :series="sparkSeries"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCardBody } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { seriesColor, ink } from './theme'

export default {
  name: 'KpiCard',
  components: { BCardBody, VueApexCharts },
  props: {
    kpi: {
      type: Object,
      required: true,
    },
    skin: {
      type: String,
      default: 'light',
    },
    // اندیس پایدار سری برای رنگ اسپارک‌لاین
    colorIndex: {
      type: Number,
      default: 0,
    },
    compact: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasCompare() {
      return this.kpi.CompareValue !== null && this.kpi.CompareValue !== undefined
    },

    variant() {
      const delta = this.kpi.Delta
      if (delta === null || delta === undefined || delta === 0) return 'secondary'
      const higherIsBetter = this.kpi.HigherIsBetter !== false
      return (delta > 0) === higherIsBetter ? 'success' : 'danger'
    },

    deltaLabel() {
      const delta = this.kpi.Delta
      if (delta === null || delta === undefined) return null
      const sign = delta > 0 ? '+' : ''
      return `${sign}${this.round(delta)}٪`
    },

    displayValue() {
      return this.compact
        ? this.formatCompact(this.kpi.Value, this.kpi.Format)
        : this.formatFull(this.kpi.Value, this.kpi.Format)
    },

    fullValue() {
      return this.formatFull(this.kpi.Value, this.kpi.Format)
    },

    compareValue() {
      return this.formatCompact(this.kpi.CompareValue, this.kpi.Format)
    },

    spark() {
      const values = this.kpi.Spark
      // یک نقطه روند نیست؛ اسپارک‌لاین تک‌نقطه‌ای گمراه‌کننده است
      return values && values.length > 1 ? values : null
    },

    sparkSeries() {
      return [{ name: this.kpi.Title, data: this.spark }]
    },

    sparkOptions() {
      const color = seriesColor(this.colorIndex, this.skin)

      return {
        chart: {
          type: 'area',
          sparkline: { enabled: true },
          animations: { enabled: false },
          fontFamily: 'IranSans, system-ui, sans-serif',
        },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.6, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100],
          },
        },
        colors: [color],
        tooltip: { enabled: false },
        // اسپارک‌لاین مقدار را نمی‌رساند؛ عدد بالا آن را می‌رساند
        markers: { size: 0 },
      }
    },
  },
  methods: {
    round(value, digits = 1) {
      const factor = 10 ** digits
      return Math.round(value * factor) / factor
    },

    group(value) {
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    formatFull(value, format) {
      if (value === null || value === undefined) return '—'
      if (format === 'percent') return `${this.round(value)}٪`
      if (format === 'price') return `${this.group(Math.round(value))} تومان`
      return this.group(Math.abs(value % 1) > 0 ? this.round(value, 2) : value)
    },

    formatCompact(value, format) {
      if (value === null || value === undefined) return '—'
      if (format === 'percent') return `${this.round(value)}٪`

      const abs = Math.abs(value)
      let text
      let unit = ''

      if (abs >= 1000000000) { text = this.round(value / 1000000000); unit = ' میلیارد' } else if (abs >= 1000000) { text = this.round(value / 1000000); unit = ' میلیون' } else if (abs >= 1000) { text = this.round(value / 1000); unit = ' هزار' } else { text = this.group(Math.abs(value % 1) > 0 ? this.round(value, 2) : value) }

      return format === 'price' ? `${text}${unit} تومان` : `${text}${unit}`
    },
  },
}
</script>

<style lang="scss" scoped>
.kpi-card {
  height: 100%;

  &__body {
    padding: 1.2rem 1.2rem 0.6rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__main {
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    color: #b9b9c3;
    margin-bottom: 0.35rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // ارقام متناسب عمداً: عدد بزرگ با tabular-nums شل به نظر می‌رسد
  &__value {
    font-size: 1.45rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__delta {
    font-size: 0.72rem;
    margin-top: 0.2rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__compare {
    color: #b9b9c3;
    margin-right: 0.35rem;
  }

  &__spark {
    margin: 0.35rem -0.6rem -0.35rem;
  }
}
</style>
